/**
 * Guscio dell'applicazione operativa.
 *
 * La navigazione è filtrata per permesso: un cameriere non vede le voci che non
 * potrebbe comunque usare. È una scelta di ergonomia, non di sicurezza — quella
 * è nel database.
 */

import { Suspense, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Building2,
  CalendarDays,
  ChefHat,
  ClipboardList,
  Clock,
  CreditCard,
  LayoutGrid,
  LogOut,
  Menu as MenuIcon,
  Package,
  Settings,
  Users,
  UtensilsCrossed,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react';
import { branding } from '@/config/branding';
import { isDemoMode } from '@/config/env';
import { PERMISSIONS, ROLE_LABELS, type Permission } from '@/domains/auth/permissions';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { cn, initials } from '@/lib/utils';
import { useActiveContext, useActiveLocation, useSession } from '@/stores/session-store';
import { Badge, Button, LoadingState, Select } from '@/components/ui/primitives';
import { ErrorBoundary } from '@/components/error-boundary';
import { BrandMark } from '@/components/brand-mark';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  /** Basta uno dei permessi elencati. */
  permission: Permission | readonly Permission[];
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/app/dashboard', label: 'Dashboard', icon: BarChart3, permission: PERMISSIONS.orgView },
  { to: '/app/sala', label: 'Sala', icon: LayoutGrid, permission: PERMISSIONS.floorView },
  { to: '/app/ordini', label: 'Ordini', icon: ClipboardList, permission: PERMISSIONS.orderView },
  { to: '/app/cucina', label: 'Cucina', icon: ChefHat, permission: PERMISSIONS.kitchenView },
  { to: '/app/cassa', label: 'Cassa', icon: CreditCard, permission: PERMISSIONS.paymentView },
  { to: '/app/prenotazioni', label: 'Prenotazioni', icon: CalendarDays, permission: PERMISSIONS.reservationView },
  { to: '/app/clienti', label: 'Clienti', icon: Users, permission: PERMISSIONS.customerView },
  { to: '/app/menu', label: 'Menu', icon: UtensilsCrossed, permission: PERMISSIONS.menuView },
  { to: '/app/magazzino', label: 'Magazzino', icon: Package, permission: PERMISSIONS.inventoryView },
  {
    to: '/app/personale',
    label: 'Personale',
    icon: Clock,
    permission: [PERMISSIONS.staffAttendance, PERMISSIONS.staffView, PERMISSIONS.staffManage],
  },
  { to: '/app/report', label: 'Report', icon: BarChart3, permission: PERMISSIONS.reportView },
  { to: '/app/impostazioni', label: 'Impostazioni', icon: Settings, permission: PERMISSIONS.locationView },
];

function ConnectionIndicator() {
  const { isOnline } = useOnlineStatus();

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        isOnline ? 'bg-success/15 text-success' : 'bg-destructive/15 text-destructive',
      )}
      // Il cambio di stato viene annunciato: in sala l'operatore deve accorgersene
      // anche senza guardare l'angolo dello schermo.
      role="status"
      aria-live="polite"
    >
      {isOnline ? <Wifi className="size-3.5" aria-hidden="true" /> : <WifiOff className="size-3.5" aria-hidden="true" />}
      {isOnline ? 'Connesso' : 'Non connesso'}
    </span>
  );
}

export function AppLayout() {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const context = useActiveContext();
  const activeLocation = useActiveLocation();
  const { user, contexts, locations, isPlatformAdmin, setActiveOrganization, setActiveLocation, signOut } =
    useSession();

  const permissions = context?.permissions ?? [];
  const visibleItems = NAV_ITEMS.filter((item) =>
    (Array.isArray(item.permission) ? item.permission : [item.permission]).some((code) => permissions.includes(code)),
  );

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/accedi', { replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <a href="#contenuto" className="skip-link">
        Salta al contenuto
      </a>

      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="flex h-16 items-center gap-3 px-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label={mobileNavOpen ? 'Chiudi menu' : 'Apri menu'}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            {mobileNavOpen ? <X aria-hidden="true" /> : <MenuIcon aria-hidden="true" />}
          </Button>

          <BrandMark className="hidden sm:flex" />

          <div className="ml-auto flex items-center gap-2">
            {isDemoMode ? (
              <Badge variant="warning" className="hidden md:inline-flex">
                Modalità dimostrativa
              </Badge>
            ) : null}
            <ConnectionIndicator />

            {contexts.length > 1 ? (
              <Select
                aria-label="Organizzazione attiva"
                className="hidden h-9 w-44 md:block"
                value={context?.organization_id ?? ''}
                onChange={(event) => void setActiveOrganization(event.target.value)}
              >
                {contexts.map((entry) => (
                  <option key={entry.organization_id} value={entry.organization_id}>
                    {entry.organization_name}
                  </option>
                ))}
              </Select>
            ) : null}

            {locations.length > 0 ? (
              <Select
                aria-label="Sede attiva"
                className="h-9 w-40 sm:w-48"
                value={activeLocation?.id ?? ''}
                onChange={(event) => setActiveLocation(event.target.value)}
              >
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </Select>
            ) : null}

            <div className="hidden items-center gap-2 border-l pl-3 sm:flex">
              <span
                className="flex size-9 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary"
                aria-hidden="true"
              >
                {initials(user?.fullName || user?.email || '?')}
              </span>
              <div className="text-xs leading-tight">
                <p className="font-medium">{user?.fullName || user?.email}</p>
                <p className="text-muted-foreground">
                  {context ? (ROLE_LABELS[context.role_code] ?? context.role_name) : '—'}
                </p>
              </div>
            </div>

            <Button variant="ghost" size="icon" aria-label="Esci" onClick={() => void handleSignOut()}>
              <LogOut aria-hidden="true" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        <nav
          aria-label="Navigazione principale"
          className={cn(
            'fixed inset-y-16 left-0 z-30 w-64 overflow-y-auto border-r bg-background p-3 transition-transform lg:static lg:inset-auto lg:translate-x-0',
            mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <ul className="space-y-1">
            {visibleItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-touch items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted',
                    )
                  }
                >
                  <item.icon className="size-4 shrink-0" aria-hidden="true" />
                  {item.label}
                </NavLink>
              </li>
            ))}
            {isPlatformAdmin ? (
              <li className="border-t pt-1">
                <NavLink
                  to="/piattaforma"
                  className="flex min-h-touch items-center gap-3 rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <Building2 className="size-4 shrink-0" aria-hidden="true" />
                  Piattaforma
                </NavLink>
              </li>
            ) : null}
          </ul>

          {activeLocation ? (
            <div className="mt-6 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
              <p className="flex items-center gap-1.5 font-medium text-foreground">
                <Building2 className="size-3.5" aria-hidden="true" />
                {activeLocation.name}
              </p>
              <p className="mt-1">
                {activeLocation.city ?? '—'} · fuso {activeLocation.timezone}
              </p>
            </div>
          ) : null}
        </nav>

        {mobileNavOpen ? (
          <div
            className="fixed inset-0 z-20 bg-black/40 lg:hidden"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
        ) : null}

        <main id="contenuto" className="min-w-0 flex-1 p-4 sm:p-6">
          <ErrorBoundary scope="app-content">
            <Suspense fallback={<LoadingState />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <footer className="border-t px-4 py-3 text-center text-xs text-muted-foreground">
        {branding.productName} · Le ricevute prodotte sono documenti gestionali e non sostituiscono lo
        scontrino fiscale.
      </footer>
    </div>
  );
}
